<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    ButtonDropdown,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row,
    Tooltip
  } from '@sveltestrap/sveltestrap';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { endOfDay, startOfDay } from 'date-fns';
  import JSZip from 'jszip';
  import QRCode from 'qrcode';

  import { BulkActionModal } from '@client/components/bulk-action-modal';
  import { Capability } from '@client/components/capability';
  import { FilterBar } from '@client/components/filter-bar';
  import { DateFilter } from '@client/components/filters/date';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { SearchFilter } from '@client/components/filters/search';
  import { Icon } from '@client/components/icon';
  import { LayoutControls } from '@client/components/layout-controls';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Pagination } from '@client/components/pagination';
  import { Spinner } from '@client/components/spinner';
  import { UserCard } from '@client/components/user-card';
  import { UserPanel } from '@client/components/user-panel';

  import { BizCardsService } from '@client/services/bizcards';
  import { DomainService } from '@client/services/domain';
  import { GroupsService } from '@client/services/groups';
  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';
  import { usersSettingsStore } from '@client/stores/page-settings';
  import { userStore } from '@client/stores/user';

  import { downloadFile } from '@client/utils/download';
  import { QueryParams } from '@client/utils/query-params';
  import { createItemSelection } from '@client/utils/selection';
  import { setTourStoreSettings } from '@client/utils/tours';
  import { buildUserExportCsv } from '@client/utils/user-export';

  import { USER_FILTERS, USER_REQUEST_PARAMS, USER_ROLES } from '@client/enums';

  import { generatePastelColors } from '@common/utils/colors';
  import { dateRangePairChanged } from '@common/utils/dates';
  import { debounce } from '@common/utils/debounce';
  import { deepCopy, merge } from '@common/utils/objects';

  // import { EDIT_STEPS } from './tours/edit';
  // import { FULL_STEPS } from './tours/full';
  // import { OVERVIEW_STEPS } from './tours/overview';
  // import { SYNC_STEPS } from './tours/sync';
  import { AdvancedFilter } from './advanced-filter';
  import { CSVFilter } from './csv-filter';
  import { HistoryModal } from './history';
  import { SenderGroupFilter } from './sender-group-filter';
  import {
    applyGroupMembers,
    archiveFilename,
    buildSignatureArchive,
    renderSignature,
    resolveGroupMembers,
    resultSetKey,
    runners,
    SelectionControls
  } from './shared';
  import { SyncModal } from './sync';

  const { addNotification } = getNotificationsContext();

  const { basepath } = getContext('config');

  const queryClient = useQueryClient();

  let filters = merge(deepCopy(USER_FILTERS), $usersSettingsStore.filters || {}, {
    search: QueryParams.get('search') || $usersSettingsStore?.filters?.search || ''
  });

  let originalFilters = deepCopy(filters);

  let queryParams = merge(deepCopy(USER_REQUEST_PARAMS), $usersSettingsStore.params || {}, {
    perPage: QueryParams.get('perpage') || $usersSettingsStore.params?.perPage || USER_REQUEST_PARAMS.perPage || 25,
    page: QueryParams.get('page') || $usersSettingsStore.params?.page || USER_REQUEST_PARAMS.page || 1,
    query: filters.search,
    debounceQuery: filters.search,
    fieldname: QueryParams.get('fieldname') || $usersSettingsStore.params?.fieldname || '',
    fieldregex: QueryParams.get('fieldregex') || $usersSettingsStore.params?.fieldregex || '',
    userparamname: QueryParams.get('userparamname') || $usersSettingsStore.params?.userparamname || '',
    userparamregex: QueryParams.get('userparamregex') || $usersSettingsStore.params?.userparamregex || '',
    block: QueryParams.get('block') || $usersSettingsStore.params?.block || '',
    notblock: QueryParams.get('notblock') || $usersSettingsStore.params?.notblock || '',
    sendergroup: String(QueryParams.get('group') || $usersSettingsStore.params?.sendergroup || '')
  });

  let originalParams = deepCopy(queryParams);
  let attributes = filters.attributes;

  let renderCardLayout = $usersSettingsStore.cards !== undefined ? $usersSettingsStore.cards : true;
  let initialized = false;

  let advancedLoaded = false;
  let bizcardFilterWasEnabled = !!filters.attributes.bizcard?.enabled;
  let bizcardUrls = {};

  let users = [];
  let filtered = [];
  let startDate = QueryParams.get('startDate');
  let endDate = QueryParams.get('endDate');
  let originalStartDate = startDate;
  let originalEndDate = endDate;
  let showFilters = true;
  let exporting = false;
  let isOpen = false;
  let updating = false;
  let showHistory = false;
  let showSync = false;
  let data = {};
  let results = 0;
  let totalRecords = 0;

  let hasFilters = false;
  let isSearching = false;

  // Emails of the selected Opensense group, or null when no group is selected. Kept
  // out of queryParams so the list is neither persisted nor written to the URL.
  let groupMembers = null;
  const groupMembersCache = {};
  let previousSearch = '';

  let selecting = false;
  let showBulkAction = false;
  let bulkAction = '';
  let bulkItems = [];
  let bulkSkipped = 0;

  // Retains a projection per tick, not just keys, so the selection survives paging.
  // email drives every payload; removed lets Disable skip the already-disabled.
  const selection = createItemSelection(
    (user) => user.email,
    ({ email, removed }) => ({ email, removed })
  );

  let lastResultSet = null;

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const fetchBizcardUsers = async () => {
    try {
      const bindings = await BizCardsService.getUserBindings();

      if (bindings !== null && !Object.keys(bindings).length) {
        bizcardUrls = {};
        return;
      }

      const response = await BizCardsService.getUsers({ perPage: 10000 });
      bizcardUrls = Object.fromEntries(
        (response?.data || []).filter((u) => u.bizcardurl).map((u) => [u.email?.toLowerCase(), u.bizcardurl])
      );
    } catch (e) {
      bizcardUrls = {};
    }
  };

  // Resolves the selected group once per name. A group that fails to resolve, or no
  // longer exists, drops the selection rather than showing an unfiltered list.
  const loadGroupMembers = async (params) => {
    const name = params.sendergroup;

    if (!name) {
      groupMembers = null;
      return;
    }

    if (!groupMembersCache[name]) {
      updating = true;

      try {
        const group = await GroupsService.getGroupById(name);

        groupMembersCache[name] = group ? await resolveGroupMembers(group) : null;
      } catch (e) {
        groupMembersCache[name] = null;
      }
    }

    if (groupMembersCache[name] === null) {
      notify($format('label.FAILURE.GROUP_MEMBERS', { values: { group: name } }), true);
      params.sendergroup = '';
      QueryParams.delete('group');
    }

    groupMembers = groupMembersCache[name] ?? null;
  };

  const queryFn = async () => {
    updating = true;

    try {
      const body = applyGroupMembers(queryParams, groupMembers);
      const response = body ? await UsersService.getUsers(body) : { data: [], headers: {} };

      if (response?.status === 500) {
        data = { data: [], headers: {} };
      } else {
        data = response;
      }

      users = assignColors(data?.data);

      filtered = users;
    } catch (e) {
      data = { data: [], headers: {} };
      users = [];
      filtered = [];
    } finally {
      updating = false;
    }

    return data;
  };

  const updateCounters = () => {
    totalRecords = (data?.headers && data?.headers['x-filtered-total']) || filtered.length;
    results = (data?.headers && data?.headers['x-search-total']) || filtered.length;
  };

  const updateQueryParams = async () => {
    const params = {
      ...queryParams,
      query: filters.search,
      debounceQuery: filters.search,
      filterByDate: !!(startDate && endDate),
      dateRange: {
        startDate: startDate ? startOfDay(startDate).getTime() : null,
        endDate: endDate ? endOfDay(endDate).getTime() : null
      },
      page: Number(queryParams.page),
      perPage: Number(queryParams.perPage)
    };

    const currentIntegrationSelections = Object.values(filters.integrations).filter(({ enabled }) => enabled);
    const currentSalesSelections = Object.values(filters.sales).filter(({ enabled }) => enabled);
    const currentRoleSelections = Object.values(filters.roles).filter(({ enabled }) => enabled);
    const currentAttributeSelections = Object.values(filters.attributes).filter(({ enabled, value }) => {
      // NOTE: setting hideAddin to false will return users with addin disabled
      if (value === 'addin') {
        params.hideAddin = enabled || null;
      }

      if (value === 'nosync') {
        params.hasBrokenSyncs = enabled;
      }

      if (value === 'unlocked') {
        params.hasUnlockedFields = enabled;
      }

      if (value === 'inactive') {
        params.hideRemoved = !enabled;
      }

      return enabled;
    });

    // Merge bizcard route bindings when filter is enabled
    if (filters.attributes.bizcard?.enabled) {
      bizcardFilterWasEnabled = true;
      const bizcardBindings = await BizCardsService.getUserBindings();
      if (bizcardBindings) {
        Object.assign(params, bizcardBindings);
      }
    } else if (bizcardFilterWasEnabled) {
      bizcardFilterWasEnabled = false;
      params.csvfilter = deepCopy(USER_REQUEST_PARAMS.csvfilter);
      params.csvtreenodebindings = [];
      params.googlegroupbindings = [];
      params.o365groupbindings = [];
      params.oktagroupbindings = [];
    }

    params.roles = currentRoleSelections.length ? currentRoleSelections.map(({ value }) => value) : [];

    params.systemFilter = currentIntegrationSelections.length
      ? currentIntegrationSelections.map(({ value }) => ({ id: value }))
      : [];

    if (currentSalesSelections.length) {
      params.sales = currentSalesSelections.some(({ value }) => value === 'tracking') ? true : null;
      params.autobcc = currentSalesSelections.some(({ value }) => value === 'autobcc') ? true : null;
    } else {
      params.sales = null;
      params.autobcc = null;
    }

    await loadGroupMembers(params);

    $usersSettingsStore.filters = filters;
    $usersSettingsStore.params = params;

    queryParams = { ...params };

    await queryFn();

    const hasGroupBindings =
      params.csvtreenodebindings.length ||
      params.googlecountrybindings.length ||
      params.googlegroupbindings.length ||
      params.o365groupbindings.length ||
      params.oktagroupbindings.length ||
      params.exclusions.googlegroupbindings.length ||
      params.exclusions.o365groupbindings.length ||
      params.exclusions.oktagroupbindings.length;

    const hasAdvancedFilters =
      params.fieldname.length || params.userparamname.length || params.block.length || hasGroupBindings;

    hasFilters = !!(
      (startDate && endDate) ||
      currentIntegrationSelections.length ||
      currentSalesSelections.length ||
      currentAttributeSelections.length ||
      currentRoleSelections.length ||
      filters.search.length ||
      params.csvfilter.data.length ||
      params.sendergroup ||
      hasAdvancedFilters
    );

    updateCounters();

    originalFilters = deepCopy(filters);
    originalParams = deepCopy(queryParams);
    originalStartDate = startDate;
    originalEndDate = endDate;

    if (isSearching) {
      isSearching = false;
    }

    initialized = true;
  };

  const assignColors = (results = []) => {
    const colors = generatePastelColors(results.length);

    let index = 0;

    return results.map((user) => {
      user.color = colors[index === colors.length ? 0 : index++];
      return user;
    });
  };

  const onChangeLayout = (which) => {
    renderCardLayout = !!which;
    $usersSettingsStore.cards = renderCardLayout;
  };

  const onChangePage = (page) => {
    queryParams = { ...queryParams, page };
  };

  const onChangeRecords = (perPage) => {
    queryParams = { ...queryParams, perPage };
  };

  const onExportUsers = async (includeDiagnostics, isAll) => {
    $appStore.loading = true;
    exporting = true;

    // NOTE: if we ever want to set a startDate for export all: merge(deepCopy(USER_REQUEST_PARAMS), { startDate: new Date(new Date().setDate(new Date().getDate() - 365)) })
    // same with including disabled users: merge(deepCopy(USER_REQUEST_PARAMS), { hideRemoved: null })!
    const exportParams = isAll ? deepCopy(USER_REQUEST_PARAMS) : queryParams;
    const params = applyGroupMembers(
      { ...exportParams, includeSigs: !!includeDiagnostics },
      isAll ? null : groupMembers
    );

    const response = params ? await UsersService.exportUsers(params) : '';

    exporting = false;
    $appStore.loading = false;

    const fields = includeDiagnostics ? null : await DomainService.getFields();
    const csv = buildUserExportCsv(response, fields, { includeDiagnostics });

    const blob = new Blob([csv], { type: 'text/csv; charset=utf-8' });
    const filename = `${$userStore.domain}-users(${isAll ? 'all' : totalRecords})${includeDiagnostics ? '-diagnostics-' : ''}-${new Date().getTime()}.csv`;

    downloadFile(blob, filename);
  };

  const onExportQRCodes = async (isAll) => {
    try {
      $appStore.loading = true;
      exporting = true;

      const response = await BizCardsService.getUsers({ perPage: totalRecords });
      const users = response.data.filter((user) => user.bizcardurl);

      const zip = new JSZip();

      await Promise.all(
        users.map(async (user) => {
          const dataUrl = await QRCode.toDataURL(user.bizcardurl, { margin: 1, width: 300 });
          const base64 = dataUrl.split(',')[1];
          const username = user.email.split('@')[0];
          zip.file(`${username}.png`, base64, { base64: true });
        })
      );

      const blob = await zip.generateAsync({ type: 'blob' });
      const filename = `${$userStore.domain}-qrcodes(${users.length})-${new Date().getTime()}.zip`;

      downloadFile(blob, filename);
    } catch (err) {
      console.error(err);
      notify($format('label.FAILURE.EXPORT'), true);
    } finally {
      exporting = false;
      $appStore.loading = false;
    }
  };

  const onClearCache = async (user) => {
    const response = await UsersService.clearCache({
      email: user.email
    });

    if (response !== 'Empty') {
      notify($format('label.FAILURE.UPDATE_USER_CACHE'), true);
    } else {
      notify($format('label.SUCCESS.UPDATE_USER_CACHE', { values: { user: user.name } }));
    }
  };

  const onSendReminder = async (user) => {
    const response = await UsersService.sendReminder({
      emails: [user.email]
    });

    if (response.status === 400) {
      notify($format('label.FAILURE.SEND_REMINDER'), true);
    } else {
      notify($format('label.SUCCESS.SEND_REMINDER', { values: { user: user.name } }));
    }
  };

  const onToggleUserState = async (user) => {
    updating = true;

    const response = await UsersService.updateUser({
      email: user.email,
      removed: user.removed,
      updateType: 'removed'
    });

    updating = false;

    if (response.status === 400) {
      notify($format('label.FAILURE.TOGGLE_USER_STATE', { values: { message: response.message } }), true);
    } else {
      if (user.removed) {
        notify($format('label.SUCCESS.TOGGLE_USER_STATE_DISABLED', { values: { user: user.name } }));
      } else {
        notify($format('label.SUCCESS.TOGGLE_USER_STATE_ENABLED', { values: { user: user.name } }));
      }
    }
  };

  const BULK_NOTIFY = {
    reminder: 'label.BULK_ACTION.NOTIFY_REMINDER',
    cache: 'label.BULK_ACTION.NOTIFY_CACHE',
    disable: 'label.BULK_ACTION.NOTIFY_DISABLE',
    signatures: 'label.BULK_ACTION.NOTIFY_SIGNATURES'
  };

  // Unlike the runners, this one accumulates, so it stays here rather than joining
  // that map of pure predicates. Reset per run in onBulkAction.
  let signatureArchive = [];

  const runSignature = async (user) => {
    const html = await renderSignature(user.email);

    if (!html) {
      return false;
    }

    signatureArchive = [...signatureArchive, { email: user.email, html }];

    return true;
  };

  const downloadSignatureArchive = async () => {
    const blob = await buildSignatureArchive(signatureArchive);

    if (blob) {
      downloadFile(blob, archiveFilename($userStore.domain, signatureArchive.length));
    }
  };

  // Disable skips users that are already disabled rather than failing them.
  const activeOnly = (selected) => selected.filter(({ removed }) => !removed);

  const onBulkAction = (action) => {
    const targets = action === 'disable' ? activeOnly(selectedUsers) : [...selectedUsers];

    // Nothing left to act on. Disable says so; the other actions bail silently since
    // this only happens when a stale selection outruns a refetch — never a user choice.
    if (!targets.length) {
      if (action === 'disable') {
        notify($format('label.BULK_ACTION.NOTHING_TO_DISABLE'));
      }
      return;
    }

    // Snapshot the run: the modal can sit open across a focus refetch, which would
    // otherwise reshape a derived target list under a run already in flight.
    bulkItems = targets;
    bulkSkipped = selectedUsers.length - targets.length;
    bulkAction = action;
    signatureArchive = [];
    showBulkAction = true;
  };

  const onBulkActionFinish = async (failed) => {
    const succeeded = bulkItems.length - failed.length;

    // Held until dismissal so a file never lands while the failure list is being read.
    if (bulkAction === 'signatures') {
      await downloadSignatureArchive();
    }

    if (succeeded) {
      notify($format(BULK_NOTIFY[bulkAction], { values: { count: succeeded } }));
    }

    // Leave only the failures selected so they can be retried directly. They stay
    // selected even if they are no longer on the current page.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    // Only disabling changes what's on screen. queryFn does not touch the counters,
    // so refresh them here or the footer keeps offering pages that no longer exist.
    if (bulkAction === 'disable') {
      await queryFn();
      updateCounters();
    }
  };

  // startDate/endDate are two-way bound from <DateFilter>; updateQueryParams is now the
  // SOLE writer of dateRange (derived from the same pair), so this handler no longer
  // mutates queryParams.dateRange. The bound values drive the reactive dateFilterChanged
  // trigger below. (Current behavior reset no paging, so reducing this drops nothing.)
  const onDateChange = () => {};

  const onViewSyncHistory = () => (showHistory = true);

  const onRunSync = async () => {
    showSync = true;
  };

  onMount(async () => {
    fetchBizcardUsers();
    await updateQueryParams();
  });

  const query = createQuery({ queryKey: ['users', queryParams], queryFn });

  const onSearchChange = debounce((search) => {
    queryClient.cancelQueries(['users', queryParams]);

    previousSearch = filters.search;

    filters.search = search;
    queryParams = { ...queryParams, page: 1, query: search, debounceQuery: search };

    if (search !== previousSearch) {
      isSearching = true;
      updateQueryParams();
    }
  }, 350);

  let searchInputValue = filters.search;

  const onAttributeClick = () => {
    queryParams = { ...queryParams, page: 1 };
  };

  const onGroupChange = (sendergroup) => {
    queryParams = { ...queryParams, page: 1, sendergroup };
  };

  $: $usersSettingsStore = setTourStoreSettings($usersSettingsStore, !!filtered.length);

  $: hasData = $usersSettingsStore?.tours?.hasData;

  $: usersTourSteps = [
    {
      steps: '',
      stepsKey: 'default',
      menuLabel: $format('label.TOURS.USERS.MENU.OVERVIEW'),
      storeKey: 'overviewHasToured',
      disabled: !hasData
    },
    {
      steps: '',
      stepsKey: 'sync',
      menuLabel: $format('label.TOURS.USERS.MENU.SYNC'),
      storeKey: 'syncHasToured',
      disabled: !hasData
    },
    {
      steps: '',
      stepsKey: 'edit',
      menuLabel: $format('label.TOURS.USERS.MENU.EDIT'),
      storeKey: 'editHasToured',
      disabled: !hasData
    },
    {
      steps: '',
      stepsKey: 'full'
    }
  ];

  $: dismissedOrToured = $usersSettingsStore?.tours?.isDismissed || $usersSettingsStore?.tours?.hasToured;

  $: configChanged =
    JSON.stringify(queryParams) !== JSON.stringify(originalParams) ||
    JSON.stringify(filters) !== JSON.stringify(originalFilters);

  // Only refetch once the range pair is complete (both ends set) or fully cleared —
  // a half-selected range (start chosen, end not yet picked) must not fire an
  // intermediate unfiltered request that could race the final filtered one.
  $: dateFilterChanged = dateRangePairChanged(startDate, endDate, originalStartDate, originalEndDate);

  $: selectedUsers = [...$selection.values()];
  // Paging keeps the selection; a different result set clears it. The null guard stops
  // the first run clearing a selection that was never made.
  $: currentResultSet = resultSetKey(queryParams, filters, startDate, endDate);
  $: if (initialized && currentResultSet !== lastResultSet) {
    if (lastResultSet !== null) {
      selection.clear();
    }

    lastResultSet = currentResultSet;
  }
  $: bulkConfig =
    {
      reminder: {
        title: $format('label.BULK_ACTION.CONFIRM_REMINDER', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_REMINDER'),
        confirmLabel: $format('label.SEND_REMINDER'),
        confirmColor: 'primary',
        progressLabel: $format('label.BULK_ACTION.PROGRESS_REMINDER'),
        batch: true
      },
      cache: {
        title: $format('label.BULK_ACTION.CONFIRM_CACHE', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_CACHE'),
        confirmLabel: $format('label.CLEAR_CACHE'),
        confirmColor: 'primary',
        batch: false
      },
      disable: {
        title: $format('label.BULK_ACTION.CONFIRM_DISABLE', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_DISABLE'),
        confirmLabel: $format('label.DISABLE_USER'),
        confirmColor: 'danger',
        batch: false
      },
      signatures: {
        title: $format('label.BULK_ACTION.CONFIRM_SIGNATURES', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_SIGNATURES'),
        confirmLabel: $format('label.BULK_ACTION.DOWNLOAD_SIGNATURES'),
        confirmColor: 'primary',
        progressKey: 'label.BULK_ACTION.PROGRESS_SIGNATURES',
        batch: false
      }
    }[bulkAction] || {};

  $: initialized && (configChanged || dateFilterChanged) && !isSearching && updateQueryParams();
</script>

<div id="users" class="page" class:with-footer={data?.data}>
  <Loader backdrop show={updating || !advancedLoaded} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.USERS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="7" sm="7" md="8">
          <h1 class="page-title">
            <span class="hide--mobile">{$format('label.MANAGE_USERS')}</span>
            <span class="hide--desktop">{$format('label.USERS')}</span>
          </h1>
        </Col>
        <Col xs="5" sm="5" md="4" class="text-right">
          <!-- <Capability action="manage" subject="staff">
            {#if dismissedOrToured}
              <Tour steps={usersTourSteps} store={usersSettingsStore} />
            {/if}
          </Capability> -->
          <ButtonDropdown class="sync-controls">
            <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem on:click={onRunSync}>{$format('label.RUN_SYNC')}</DropdownItem>
              <DropdownItem divider></DropdownItem>
              <DropdownItem on:click={onViewSyncHistory}>{$format('label.VIEW_SYNC_HISTORY')}</DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="toolbar">
      <Container fluid>
        <!-- <Capability action="manage" subject="staff">
          {#if !dismissedOrToured}
            <Tour
              steps={usersTourSteps}
              store={usersSettingsStore}
              tourHeader={$format('label.TOURS.USERS.ALERT.HEADER')}
              tourDescription={$format('label.TOURS.USERS.ALERT.DESCRIPTION')}
              marginTop="mt-0"
              marginBottom="mb-4"
            />
          {/if}
        </Capability> -->
        <Row>
          <Col lg={7} sm={12}>
            <FilterBar bind:show={showFilters}>
              <div class="toolbar-left" class:collapsed={!showFilters}>
                <SearchFilter bind:search={searchInputValue} onChange={onSearchChange} />
                <MultiSelectFilter
                  bind:filter={filters.roles}
                  label={$format('label.ROLES')}
                  param="roles"
                  tooltipLabel={$format('label.SELECTED_ROLES')}
                >
                  <span class="filter-label" slot="label" let:config>
                    <span class="filter-color" style="background-color: {USER_ROLES[config.value]?.color};" />
                    {$format(config.label)}
                  </span>
                </MultiSelectFilter>
                <MultiSelectFilter
                  bind:filter={filters.integrations}
                  label={$format('label.INTEGRATIONS')}
                  param="integrations"
                  tooltipLabel={$format('label.SELECTED_INTEGRATIONS')}
                />
                <MultiSelectFilter
                  bind:filter={filters.sales}
                  label={$format('label.SALES')}
                  param="sales"
                  tooltipLabel={$format('label.SELECTED_SALES')}
                />
                <MultiSelectFilter
                  bind:filter={attributes}
                  onClick={onAttributeClick}
                  label={$format('label.ATTRIBUTES')}
                  param="attr"
                  tooltipLabel={$format('label.SELECTED_ATTRIBUTES')}
                />
                <SenderGroupFilter selected={queryParams.sendergroup} onChange={onGroupChange} />
                <DateFilter
                  bind:startDate
                  bind:endDate
                  bind:isOpen
                  {onDateChange}
                  showPresetsOnly={$appStore.mobile}
                  isRange
                  isMultipane
                />
                <CSVFilter bind:params={queryParams} />
                <AdvancedFilter bind:params={queryParams} bind:initialized={advancedLoaded} />
              </div>
            </FilterBar>
          </Col>
          <Col lg={5} sm={12}>
            <div class="toolbar-right">
              <!-- Hidden while selecting: the selected count is its own "n of n" and two together read as a conflict. -->
              {#if !selecting && filtered.length !== results}
                <small class="count">
                  {$format('label.MATCHES_X_OF_X_USERS', {
                    values: {
                      filtered: Number(totalRecords).toLocaleString(),
                      total: Number(results).toLocaleString()
                    }
                  })}
                </small>
              {/if}
              {#if filtered.length}
                <SelectionControls
                  bind:selecting
                  {selection}
                  total={totalRecords}
                  id="users"
                  onAction={onBulkAction}
                  onSelectAll={() => selection.add(filtered)}
                />
              {/if}
              <Dropdown id="export-dropdown">
                <DropdownToggle size="sm" class="export-dropdown" disabled={!filtered.length || exporting}>
                  {#if exporting}
                    <Spinner styles="border-width: 2px; color: #0087ff; height: 14px; width: 14px;" show inline />
                  {:else}
                    <Icon name="download" />
                    <span class="visually-hidden">{$format('label.EXPORT')}</span>
                  {/if}
                </DropdownToggle>
                <DropdownMenu end>
                  <Capability action="manage" subject="bizcards">
                    {#if hasFilters}
                      <DropdownItem on:click={() => onExportQRCodes()}>
                        {@html $format('label.EXPORT_QRCODES_OPTION', {
                          values: { total: Number(totalRecords).toLocaleString() }
                        })}
                      </DropdownItem>
                    {/if}
                    <DropdownItem on:click={() => onExportQRCodes(true)}>
                      {@html $format('label.EXPORT_QRCODES_ALL')}
                    </DropdownItem>
                    <DropdownItem divider />
                  </Capability>
                  {#if hasFilters}
                    <DropdownItem on:click={() => onExportUsers()}>
                      {@html $format('label.EXPORT_USERS_OPTION', {
                        values: { total: Number(totalRecords).toLocaleString() }
                      })}
                    </DropdownItem>
                    <DropdownItem on:click={() => onExportUsers(true)}>
                      {@html $format('label.EXPORT_USERS_DIAGNOSTICS_OPTION', {
                        values: { total: Number(totalRecords).toLocaleString() }
                      })}
                    </DropdownItem>
                    <DropdownItem divider />
                  {/if}
                  <DropdownItem on:click={() => onExportUsers(false, true)}>
                    {@html $format('label.EXPORT_USERS_ALL')}
                  </DropdownItem>
                  <DropdownItem on:click={() => onExportUsers(true, true)}>
                    {@html $format('label.EXPORT_USERS_ALL_DIAGNOSTICS')}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              <Tooltip target="export-dropdown">
                {$format('label.EXPORT')}
              </Tooltip>

              <LayoutControls {renderCardLayout} iconOther="os-icon-columns" onChange={onChangeLayout} />
            </div>
          </Col>
        </Row>
      </Container>
    </div>

    <div class="list">
      <Container fluid>
        <Row>
          <Col>
            {#if filtered.length}
              {#if renderCardLayout}
                <div class="user-cards">
                  {#each filtered as user}
                    <UserCard
                      data={user}
                      bizcardurl={bizcardUrls[user.email?.toLowerCase()]}
                      {onClearCache}
                      {onSendReminder}
                      {onToggleUserState}
                      {selecting}
                      selected={$selection.has(user.email)}
                      onSelect={selection.toggle}
                    />
                  {/each}
                </div>
              {:else}
                <div class="user-panels">
                  {#each filtered as user}
                    <UserPanel
                      data={user}
                      bizcardurl={bizcardUrls[user.email?.toLowerCase()]}
                      {onClearCache}
                      {onSendReminder}
                      {onToggleUserState}
                      {selecting}
                      selected={$selection.has(user.email)}
                      onSelect={selection.toggle}
                    />
                  {/each}
                </div>
              {/if}
            {:else if initialized}
              <NoMatches message={$format('label.MESSAGE.NO_MATCHES_USERS')} />
            {/if}
          </Col>
        </Row>
      </Container>
    </div>
  </div>
  {#if initialized && data?.data?.length}
    <div class="page-footer">
      <Pagination
        bind:currentPage={queryParams.page}
        bind:perPage={queryParams.perPage}
        {totalRecords}
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

{#if showHistory}
  <HistoryModal bind:show={showHistory} on:close={() => (showHistory = false)} />
{/if}

{#if showSync}
  <SyncModal bind:show={showSync} />
{/if}

{#if showBulkAction}
  <BulkActionModal
    bind:show={showBulkAction}
    items={bulkItems}
    title={bulkConfig.title}
    warning={bulkConfig.warning}
    note={bulkSkipped ? $format('label.BULK_ACTION.SKIPPED_DISABLED', { values: { count: bulkSkipped } }) : ''}
    confirmLabel={bulkConfig.confirmLabel}
    confirmColor={bulkConfig.confirmColor}
    progressLabel={bulkConfig.progressLabel}
    batch={bulkConfig.batch}
    progressKey={bulkConfig.progressKey || 'label.BULK_ACTION.PROGRESS'}
    onRun={(target) => (bulkAction === 'signatures' ? runSignature(target) : runners[bulkAction](target))}
    onFinish={onBulkActionFinish}
  />
{/if}

<style lang="scss" src="./users.scss"></style>
